// src/pages/seo/components/ImagesTab.tsx
import React from 'react';
import { useSaveSeo } from '@/hooks/seo/useSeoSettings';
import { toast } from '@/hooks/use-toast';

type ImageRec = {
  id?: string;
  page_slug?: string;
  section?: string;
  asset_url: string;
  alt?: string;
  caption?: string;
  tags?: string[];
  og_candidate?: boolean;
  updated_at?: string;
};

const SECTIONS = ['hero','about','gallery','recognitions','pricing','contact','other'] as const;

function adminHeaders(): HeadersInit {
  try {
    const ok =
      typeof window !== 'undefined' &&
      localStorage.getItem('fca_admin_authed_v1') === '1';
    return ok ? { 'x-admin-ok': '1' } : {};
  } catch { return {}; }
}

async function fetchBundle(): Promise<{ images: ImageRec[]; pages: any[] }> {
  const r = await fetch('/api/seo?action=getBundle', { credentials: 'include', headers: adminHeaders() });
  const j = await r.json();
  if (!j.ok) throw new Error(j.error || 'failed');
  return { images: j.images || [], pages: j.pages || [] };
}

async function crawlAllImages(): Promise<{ ok: boolean; added: number; scanned: number }> {
  const r = await fetch('/api/seo', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...adminHeaders() },
    body: JSON.stringify({ action: 'images.crawlAll' })
  });
  const j = await r.json();
  if (!j.ok) throw new Error(j.error || 'crawl failed');
  return j;
}

export function ImagesTab({ onDirtyChange }: { data?: any; onDirtyChange?: (dirty: boolean) => void }) {
  const save = useSaveSeo();
  const [busy, setBusy] = React.useState(false);
  const [images, setImages] = React.useState<ImageRec[]>([]);
  const [pages, setPages] = React.useState<{ slug: string }[]>([]);
  const [pageFilter, setPageFilter] = React.useState<string>('all');
  const [sectionFilter, setSectionFilter] = React.useState<string>('all');
  const [q, setQ] = React.useState<string>('');
  const [dirtyImages, setDirtyImages] = React.useState<Set<string>>(new Set());
  const [editedImages, setEditedImages] = React.useState<Map<string, Partial<ImageRec>>>(new Map());

  React.useEffect(() => {
    onDirtyChange?.(dirtyImages.size > 0);
  }, [dirtyImages.size, onDirtyChange]);

  const load = React.useCallback(async () => {
    const { images, pages } = await fetchBundle();
    setImages(images);
    setPages(pages);
    if (pageFilter === 'all' && pages?.length) setPageFilter(pages[0].slug || 'all');
    setDirtyImages(new Set());
    setEditedImages(new Map());
  }, [pageFilter]);

  React.useEffect(() => { load().catch(()=>{}); }, []); // initial

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyImages.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirtyImages]);

  const filtered = images.filter((im) => {
    if (pageFilter !== 'all' && (im.page_slug || '/') !== pageFilter) return false;
    if (sectionFilter !== 'all' && (im.section || 'other') !== sectionFilter) return false;
    if (q) {
      const hay = [im.asset_url, im.alt, im.caption, (im.tags || []).join(',')].join(' ').toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4">
      {/* controls */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-xs block mb-1">Page</label>
          <select className="border rounded p-2"
                  value={pageFilter}
                  onChange={(e)=>setPageFilter(e.target.value)}>
            <option value="all">All</option>
            {pages.map((p:any)=>(
              <option key={p.slug} value={p.slug}>{p.slug}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs block mb-1">Section</label>
          <select className="border rounded p-2"
                  value={sectionFilter}
                  onChange={(e)=>setSectionFilter(e.target.value)}>
            <option value="all">All</option>
            {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs block mb-1">Search</label>
          <input className="border rounded p-2 w-full" placeholder="alt, tags, url…" value={q} onChange={e=>setQ(e.target.value)} />
        </div>
        <div className="ml-auto">
          <button
            className="border rounded px-3 py-2"
            disabled={busy}
            onClick={async ()=>{
              setBusy(true);
              try {
                const j = await crawlAllImages();
                await load();
                alert(`Imported images: +${j.added} (scanned ${j.scanned} pages)`);
              } catch (e:any) {
                alert(e?.message || 'Import failed');
              } finally { setBusy(false); }
            }}
          >{busy ? 'Importing…' : 'Import images from site'}</button>
        </div>
      </div>

      {/* grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.length === 0 ? (
          <div className="text-sm text-gray-600">No images yet. Click “Import images from site”.</div>
        ) : filtered.map((im) => {
          const isDirty = dirtyImages.has(im.asset_url);
          const edited = editedImages.get(im.asset_url) || {};
          const currentAlt = edited.alt !== undefined ? edited.alt : im.alt;
          const currentSection = edited.section !== undefined ? edited.section : im.section;

          return (
            <div key={im.asset_url} className="border rounded p-2 bg-white">
              <div className="flex items-start gap-3">
                <img src={im.asset_url} alt={im.alt || ''} className="w-24 h-24 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 truncate">{im.asset_url}</div>
                  <div className="text-xs text-gray-500">Page: {im.page_slug || '/'}</div>
                  <div className="text-xs text-gray-500">Section: {im.section || 'other'}</div>
                </div>
              </div>

              <div className="mt-2">
                <label className="text-xs">Alt text</label>
                <textarea
                  className="w-full border rounded p-2 text-sm"
                  rows={2}
                  value={currentAlt || ''}
                  onChange={(e) => {
                    const alt = e.target.value;
                    setEditedImages(prev => new Map(prev).set(im.asset_url, { ...edited, alt }));
                    setDirtyImages(prev => new Set(prev).add(im.asset_url));
                  }}
                />
              </div>

              <div className="mt-2 flex items-center gap-2">
                <label className="text-xs">Section</label>
                <select
                  className="border rounded p-1 text-sm"
                  value={currentSection || 'other'}
                  onChange={(e) => {
                    const section = e.target.value;
                    setEditedImages(prev => new Map(prev).set(im.asset_url, { ...edited, section }));
                    setDirtyImages(prev => new Set(prev).add(im.asset_url));
                  }}
                >
                  {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="mt-2">
                <button
                  className="border rounded px-3 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isDirty}
                  onClick={async () => {
                    const imageData = {
                      asset_url: im.asset_url,
                      page_slug: im.page_slug,
                      ...edited
                    };
                    save.mutate(
                      { image: imageData },
                      {
                        onSuccess: () => {
                          setDirtyImages(prev => {
                            const next = new Set(prev);
                            next.delete(im.asset_url);
                            return next;
                          });
                          setEditedImages(prev => {
                            const next = new Map(prev);
                            next.delete(im.asset_url);
                            return next;
                          });
                          toast({
                            title: "Image saved",
                            description: "Image metadata has been saved successfully.",
                          });
                        }
                      }
                    );
                  }}
                >
                  {isDirty ? 'Save Changes' : 'Saved'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
